// The drum loop pickers' list. The manifest keeps the categories the library
// was built with: the 33 hand-picked loops carry hand-written ones and the rest
// DrumGenius's own, so one feel can sit under several names ('Swing' beside
// 'Medium Swing' and 'Fast Swing', 'Shuffle' beside 'Blues Shuffle', a bossa
// nova under 'Latin' beside 'Brazilian'), and the loops come in file order, so
// a list that headed each change of category headed 'Afro-Cuban' 29 times.
// Every picker reads its rows from here: each section once, in a fixed order,
// a section's loops slow to fast.

import type { DrumLoop } from '@/audio/drumLoops';
import type { SoundMenuSearch } from './soundMenu';

/** The sections, in menu order, with the manifest categories each holds. */
const SECTIONS: readonly { name: string; categories: readonly string[] }[] = [
  {
    name: 'Swing',
    categories: [
      'Swing',
      'Slow Swing',
      'Med. Slow Swing',
      'Medium Swing',
      'Med. Up Swing',
      'Fast Swing',
      'Very Fast Swing',
    ],
  },
  { name: 'Ballad', categories: ['Ballad'] },
  { name: 'Two feel', categories: ['Two feel'] },
  { name: 'Jazz waltz', categories: ['Jazz waltz', '3/4'] },
  { name: 'Swing and clave', categories: ['Swing + Clave'] },
  { name: 'Shuffle', categories: ['Shuffle', 'Blues Shuffle'] },
  { name: 'New Orleans and traditional', categories: ['New Orleans & Traditional'] },
  // The library's two 'Latin' loops are a bossa nova and a samba.
  { name: 'Brazilian', categories: ['Brazilian', 'Latin'] },
  { name: 'Afro-Cuban', categories: ['Afro-Cuban'] },
  { name: 'Caribbean', categories: ['Caribbean'] },
  { name: 'Boogaloo', categories: ['Boogaloo'] },
  { name: 'Straight 8ths', categories: ['Straight 8ths'] },
  { name: 'Funk, R&B and hip hop', categories: ['Funk', 'R&B / Funk / Hip Hop'] },
  { name: 'Pop, rock and country', categories: ['Pop / Rock / Country'] },
  { name: 'World', categories: ['World'] },
  { name: 'Odd time', categories: ['Odd time'] },
  { name: 'Polyrhythms', categories: ['Polyrhythms'] },
];
const OTHER = 'Other';

const fold = (category: string) => category.trim().toLowerCase();
const SECTION_OF = new Map(
  SECTIONS.flatMap((section, order) =>
    section.categories.map((category) => [fold(category), { name: section.name, order }] as const),
  ),
);

/**
 * The section a loop is listed under. A category the table does not know (an
 * uploaded loop's, typed by an admin) is a section of its own, after the
 * known ones; a loop with none goes to Other, last.
 */
export function drumLoopSection(category: string | undefined): string {
  const known = SECTION_OF.get(fold(category ?? ''));
  if (known) return known.name;
  const name = category?.trim() ?? '';
  return !name || fold(name) === fold(OTHER) ? OTHER : name;
}

/** Where a section sits: the table's order, then unknown names A–Z, then Other. */
function sectionRank(name: string): [number, string] {
  const known = SECTIONS.findIndex((section) => section.name === name);
  if (known >= 0) return [known, ''];
  return [name === OTHER ? SECTIONS.length + 1 : SECTIONS.length, name.toLowerCase()];
}

/** A loop's row: its name and tempo, and a caveat when it will drift. */
export function drumLoopOptionLabel(loop: DrumLoop): string {
  // Neither tempo source put this loop on the beat grid, so it will drift: say
  // so in the picker rather than let it sound broken.
  return `${loop.label} · ♩=${Math.round(loop.bpm)}${
    loop.tempoVerified === false ? ' · ⚠ tempo unverified' : ''
  }`;
}

export interface DrumLoopOption {
  id: string;
  label: string;
  group: string;
}

/** Every loop as a picker row, grouped by section, each section slow to fast. */
export function drumLoopOptions(loops: readonly DrumLoop[]): DrumLoopOption[] {
  return loops
    .map((loop) => ({ loop, section: drumLoopSection(loop.category) }))
    .sort((a, b) => {
      const [aRank, aName] = sectionRank(a.section);
      const [bRank, bName] = sectionRank(b.section);
      return (
        aRank - bRank ||
        aName.localeCompare(bName) ||
        a.loop.bpm - b.loop.bpm ||
        a.loop.label.localeCompare(b.loop.label, undefined, { numeric: true })
      );
    })
    .map(({ loop, section }) => ({
      id: loop.key,
      label: drumLoopOptionLabel(loop),
      group: section,
    }));
}

/** Every loop picker's search: what it is called, and what it says when
 * nothing matches. */
export const DRUM_LOOP_SEARCH: SoundMenuSearch = {
  label: 'Search loops',
  empty: 'No loops found',
};
